import { useQueryClient } from "@tanstack/react-query";
import { Download, KeyRound, Upload } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ApiError } from "@/lib/api";
import { keys, useKeystore } from "@/lib/queries";
import { formatBytes, formatDate } from "@/lib/utils";
import { ConfirmDialog, CopyButton, Mono, toastError } from "./common";
import { Button, Card, CardHeader, Dialog, Field, Input, Spinner } from "./ui";

/** Parses a Gradle key.properties file. */
function parseKeyProperties(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line || line.startsWith("#") || line.startsWith("!")) continue;
    const m = /^([^=:\s]+)\s*[=:]\s*(.*)$/.exec(line);
    if (m) out[m[1]!] = m[2]!.replace(/\\(.)/g, "$1");
  }
  return out;
}

function UploadDialog({ appId, open, onOpenChange }: { appId: string; open: boolean; onOpenChange: (o: boolean) => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const propsInput = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [storePassword, setStorePassword] = useState("");
  const [keyAlias, setKeyAlias] = useState("");
  const [keyPassword, setKeyPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("storePassword", storePassword);
      form.append("keyAlias", keyAlias);
      form.append("keyPassword", keyPassword || storePassword);
      form.append("file", file);
      const res = await fetch(`/api/v1/apps/${appId}/android-keystore`, { method: "PUT", body: form, credentials: "same-origin" });
      const data = await res.json();
      if (!res.ok) throw new ApiError(res.status, data.error, data.message);
      toast.success(t("common.saved"));
      void qc.invalidateQueries({ queryKey: keys.keystore(appId) });
      onOpenChange(false);
      setFile(null);
      setStorePassword("");
      setKeyPassword("");
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("keystore.upload")}
      description={t("keystore.uploadHelp")}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button onClick={submit} loading={busy} disabled={!file || !storePassword || !keyAlias}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t("keystore.file")}>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => fileInput.current?.click()}>
              {t("admin.chooseFile")}
            </Button>
            <span className="truncate text-sm text-fg-muted">{file ? `${file.name} (${formatBytes(file.size)})` : ".jks / .keystore"}</span>
          </div>
          <input ref={fileInput} type="file" accept=".jks,.keystore,.p12,.pfx" className="hidden" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        </Field>
        <div className="flex items-center justify-between gap-2 rounded-md bg-muted/60 px-3 py-2 text-sm">
          <span className="text-fg-muted">{t("keystore.importProperties")}</span>
          <Button variant="outline" size="sm" onClick={() => propsInput.current?.click()}>
            key.properties
          </Button>
          <input
            ref={propsInput}
            type="file"
            accept=".properties,text/plain"
            className="hidden"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (!f) return;
              const p = parseKeyProperties(await f.text());
              if (p.storePassword) setStorePassword(p.storePassword);
              if (p.keyPassword) setKeyPassword(p.keyPassword);
              if (p.keyAlias) setKeyAlias(p.keyAlias);
            }}
          />
        </div>
        <Field label="storePassword" htmlFor="ks-store">
          <Input id="ks-store" type="password" autoComplete="off" value={storePassword} onChange={(e) => setStorePassword(e.target.value)} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="keyAlias" htmlFor="ks-alias">
            <Input id="ks-alias" value={keyAlias} onChange={(e) => setKeyAlias(e.target.value)} className="font-mono" />
          </Field>
          <Field label="keyPassword" help={t("keystore.keyPasswordHelp")} htmlFor="ks-key">
            <Input id="ks-key" type="password" autoComplete="off" value={keyPassword} onChange={(e) => setKeyPassword(e.target.value)} />
          </Field>
        </div>
      </div>
    </Dialog>
  );
}

export function KeystoreCard({ appId, canManage }: { appId: string; canManage: boolean }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const keystore = useKeystore(appId);
  const [open, setOpen] = useState(false);
  const base = `/api/v1/apps/${appId}/android-keystore`;
  const curl = `curl -H "Authorization: Bearer $SHIPYARD_TOKEN" \\\n  -o upload-keystore.zip "${location.origin}${base}/download"`;

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <KeyRound className="h-4 w-4" />
            {t("keystore.title")}
          </span>
        }
        description={t("keystore.description")}
        actions={
          canManage && (
            <Button size="sm" variant={keystore.data ? "outline" : "primary"} onClick={() => setOpen(true)}>
              <Upload className="h-3.5 w-3.5" />
              {keystore.data ? t("keystore.replace") : t("keystore.upload")}
            </Button>
          )
        }
      />
      <div className="space-y-4 p-5 text-sm">
        {keystore.isLoading ? (
          <Spinner />
        ) : !keystore.data ? (
          <p className="text-fg-muted">{t("keystore.none")}</p>
        ) : (
          <>
            <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-[auto_1fr]">
              <dt className="text-fg-muted">{t("keystore.file")}</dt>
              <dd>
                <Mono>{keystore.data.fileName}</Mono> <span className="text-fg-muted">({formatBytes(keystore.data.size)})</span>
              </dd>
              <dt className="text-fg-muted">keyAlias</dt>
              <dd>
                <Mono>{keystore.data.keyAlias}</Mono>
              </dd>
              <dt className="text-fg-muted">SHA-256</dt>
              <dd className="break-all font-mono text-xs">{keystore.data.sha256}</dd>
              <dt className="text-fg-muted">{t("keystore.updated")}</dt>
              <dd>
                {formatDate(keystore.data.updatedAt, i18n.language)}
                {keystore.data.uploadedBy && ` · ${keystore.data.uploadedBy.name}`}
              </dd>
            </dl>
            {canManage && (
              <>
                <div className="flex flex-wrap gap-2">
                  <a href={`${base}/download?format=zip`}>
                    <Button size="sm">
                      <Download className="h-3.5 w-3.5" />
                      {t("keystore.downloadZip")}
                    </Button>
                  </a>
                  <a href={`${base}/download?format=jks`}>
                    <Button size="sm" variant="outline">
                      {keystore.data.fileName}
                    </Button>
                  </a>
                  <a href={`${base}/download?format=properties`}>
                    <Button size="sm" variant="outline">
                      key.properties
                    </Button>
                  </a>
                  <ConfirmDialog
                    danger
                    title={t("keystore.delete")}
                    description={t("common.irreversible")}
                    confirmLabel={t("common.delete")}
                    onConfirm={async () => {
                      const res = await fetch(base, { method: "DELETE", credentials: "same-origin" });
                      if (!res.ok) throw new Error((await res.json()).message);
                      void qc.invalidateQueries({ queryKey: keys.keystore(appId) });
                    }}
                    trigger={(o) => (
                      <Button size="sm" variant="ghost" onClick={o}>
                        {t("common.delete")}
                      </Button>
                    )}
                  />
                </div>
                <div>
                  <div className="mb-1.5 flex items-center justify-between">
                    <p className="text-xs font-medium text-fg-muted">{t("keystore.cli")}</p>
                    <CopyButton value={curl} />
                  </div>
                  <pre className="overflow-x-auto rounded-md bg-slate-950 p-3 text-xs text-slate-200">{curl}</pre>
                </div>
              </>
            )}
          </>
        )}
      </div>
      {canManage && <UploadDialog appId={appId} open={open} onOpenChange={setOpen} />}
    </Card>
  );
}
